import React from 'react';

import { AminoAcid } from './AminoAcid';
import type { Shm } from '../../../generated/generatedOpenAPI';
import { cn } from '../../../lib/utils';

/**
 * SequenceRegion Component
 *
 * Renders a region of amino acids (FWR or CDR) with:
 * - Individual amino acid rendering
 * - Liability position highlighting
 * - SHM (somatic hypermutation) position highlighting
 * - Search match highlighting
 * - Click handling for region selection
 */

const s = {
  region: 'whitespace-nowrap cursor-pointer rounded-sm px-[1px]',
} as const;

interface SequenceRegionProps {
  regionSeq: string;
  isCDR: boolean;
  isHighlighted: boolean;
  regionKey: string;
  onRegionClick?: (region: string | null) => void;
  liabilityPositions: Map<number, string>; // position -> severity
  shmPositions: Map<number, Shm>; // position -> SHM annotation
  startPosition: number; // starting position of this region in the full sequence
  searchQuery?: string;
  fullSequence?: string; // full sequence to search within
  bgColor?: string; // Tailwind background class for IMGT-style region tinting
}

export const SequenceRegion = React.memo<SequenceRegionProps>(
  ({ regionSeq, isCDR, isHighlighted, regionKey, onRegionClick, liabilityPositions, shmPositions, startPosition, searchQuery, fullSequence, bgColor }) => {
    const handleClick = React.useCallback(() => {
      onRegionClick?.(isHighlighted ? null : regionKey);
    }, [isHighlighted, regionKey, onRegionClick]);

    // Find search match positions in the full sequence
    const searchMatchPositions = React.useMemo(() => {
      const positions = new Set<number>();
      if (searchQuery && fullSequence && searchQuery.trim()) {
        const searchLower = searchQuery.toLowerCase();
        const seqLower = fullSequence.toLowerCase();
        let index = seqLower.indexOf(searchLower);
        while (index !== -1) {
          for (let i = 0; i < searchQuery.length; i++) {
            positions.add(index + i);
          }
          index = seqLower.indexOf(searchLower, index + 1);
        }
      }
      return positions;
    }, [searchQuery, fullSequence]);

    return (
      <div className={cn(s.region, bgColor)} onClick={handleClick}>
        {regionSeq.split('').map((aa, idx) => {
          const absolutePosition = startPosition + idx;
          const liabilitySeverity = liabilityPositions.get(absolutePosition);
          const shm = shmPositions.get(absolutePosition);
          const isSearchMatch = searchMatchPositions.has(absolutePosition);

          return <AminoAcid key={absolutePosition} aa={aa} isCDR={isCDR} liabilitySeverity={liabilitySeverity} isSearchMatch={isSearchMatch} shm={shm} />;
        })}
      </div>
    );
  },
);

SequenceRegion.displayName = 'SequenceRegion';
